import React, { useMemo, useState } from "react";
import {
  Folder,
  TrendingUp,
  CheckSquare,
  ExternalLink,
  Scale,
  BellRing,
} from "lucide-react";
import { isPast, startOfDay, parseISO } from "date-fns";
import { useEtatGlobal, getCabinetIdFilter } from "../EtatGlobal";
import { authFetch } from "../../HttpClient";
import DragDocTransitionOverlay from "../Lead/DragDocTransitionOverlay.js";

const LeadIcon = ({ size = 24, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M19.07 4.93l-1.41 1.41" />
    <path d="M15.17 15.17a4 4 0 0 0-5.66-5.66L4 14l6 6 5.17-4.83z" />
  </svg>
);

/* ============================================================
   ATOMS — blocs élémentaires non décomposables
============================================================ */

const Dot = ({ colorClass }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${colorClass}`}
  />
);

const BigNumber = ({ value }) => (
  <span className="text-[30px] font-bold text-[#840040] leading-none">
    {value}
  </span>
);

const SubLabel = ({ children }) => (
  <span className="text-xs text-gray-400">{children}</span>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2">
    <Icon size={15} className="text-gray-400" />
    <span className="text-xs font-semibold text-gray-500">{children}</span>
  </div>
);

// En-tête de carte : titre + icône "ouvrir" en haut à droite
const CardHeader = ({ icon, children }) => (
  <div className="flex justify-between items-start">
    <SectionTitle icon={icon}>{children}</SectionTitle>
    <span className="text-gray-300">
      <ExternalLink size={14} />
    </span>
  </div>
);

const InitialsAvatar = ({ initials }) => (
  <div className="w-9 h-9 rounded-full bg-[#840040] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
    {initials}
  </div>
);

const OpenButton = ({ onClick }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick && onClick();
    }}
    className="flex items-center gap-1.5 px-3.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50 transition-colors"
  >
    <ExternalLink size={14} />
    Ouvrir le suivi
  </button>
);

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-7 text-center">
    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mb-2.5">
      <span className="text-gray-400 text-base font-bold">—</span>
    </div>
    <p className="text-xs text-gray-400">{message}</p>
  </div>
);

/* ============================================================
   MOLECULES — combinaisons d'atomes
============================================================ */

const DotRow = ({ label, value, colorClass }) => (
  <div className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center gap-2">
      <Dot colorClass={colorClass} />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <strong className="text-xs text-gray-800">{value}</strong>
  </div>
);

const RelanceRow = ({ name, type, createdAt, onIntercept }) => (
  <div className="flex justify-between items-center gap-3 py-3 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <Dot colorClass="bg-amber-400" />
      <span className="text-xs text-gray-700 truncate">{name}</span>
      {type && (
        <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0 truncate max-w-[160px]">
          {type}
        </span>
      )}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="text-[10px] text-gray-400">
        Créée le{" "}
        {new Date(createdAt).toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
        })}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onIntercept && onIntercept();
        }}
        className="text-[10px] font-semibold px-2.5 py-1 rounded-full border border-[#840040]/30 text-[#840040] hover:bg-[#840040]/5 transition-colors"
      >
        Intercepter
      </button>
    </div>
  </div>
);

const LegendItem = ({ colorClass, label }) => (
  <div className="flex items-center gap-1.5">
    <Dot colorClass={colorClass} />
    <span className="text-[10px] text-gray-500">{label}</span>
  </div>
);

const PipelineRow = ({ label, value, max }) => (
  <div className="flex items-center gap-3 mb-3 last:mb-0">
    <span className="text-[10px] text-gray-500 w-28 flex-shrink-0">
      {label}
    </span>
    <div className="flex-1 h-1.5 bg-[#f0ebe4] rounded-full overflow-hidden">
      <div
        className="h-full bg-[#840040] rounded-full"
        style={{ width: `${(value / Math.max(1, max)) * 100}%` }}
      />
    </div>
    <span className="text-[10px] font-semibold text-gray-800 w-4 text-right flex-shrink-0">
      {value}
    </span>
  </div>
);

const RankRow = ({ rank, initials, name, retard, max }) => (
  <div className="flex items-center gap-4 mb-1 last:mb-0 px-3 py-2 rounded-lg transition-colors hover:bg-[#fff6ee] cursor-default">
    <span className="text-gray-300 text-xs w-4 text-center flex-shrink-0">
      {rank}
    </span>
    <InitialsAvatar initials={initials} />
    <span className="flex-1 text-xs font-medium text-gray-800 truncate min-w-0">
      {name}
    </span>
    <div className="flex-[2] h-2 bg-[#f0ebe4] rounded-full overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{
          width: `${(retard / max) * 100}%`,
          background: "linear-gradient(to right, #f59e0b, #dc2626)",
        }}
      />
    </div>
    <span className="text-xs font-bold text-red-600 min-w-[75px] text-right">
      {retard}{" "}
      <span className="font-normal text-gray-400 text-[10px]">retard</span>
    </span>
  </div>
);

const TriColorBar = ({ done, inProgress, late, className = "mt-5" }) => {
  const total = done + inProgress + late;
  if (total === 0) {
    return <div className={`h-2.5 rounded-full bg-gray-100 ${className}`} />;
  }
  return (
    <div className={`flex h-2.5 rounded-full overflow-hidden ${className}`}>
      <div style={{ flex: done }} className="bg-green-500" />
      <div style={{ flex: inProgress }} className="bg-amber-400" />
      <div style={{ flex: late }} className="bg-red-500" />
    </div>
  );
};

const BilanSection = ({
  label,
  pct,
  done,
  inProgress,
  late,
  total,
  lateLabel = "retard",
}) => (
  <div className="pt-4 first:pt-0">
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
      {label}
    </p>
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-14">
        <span className="text-lg font-bold text-[#840040] leading-none">
          {pct}%
        </span>
        <p className="text-[9px] text-gray-400 mt-0.5">
          {done} / {total}
        </p>
      </div>
      <div className="flex-1">
        <TriColorBar
          done={done}
          inProgress={inProgress}
          late={late}
          className=""
        />
      </div>
    </div>
    <div className="flex gap-3 mt-2 flex-wrap">
      <LegendItem colorClass="bg-green-500" label={`${done} terminés`} />
      <LegendItem colorClass="bg-amber-400" label={`${inProgress} en cours`} />
      <LegendItem colorClass="bg-red-500" label={`${late} ${lateLabel}`} />
    </div>
  </div>
);

/* ============================================================
   ORGANISMS — sections complètes
============================================================ */

const Card = ({ children, onClick }) => (
  <div
    className="bg-white rounded-2xl p-6 shadow-sm"
    style={{ cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    {children}
  </div>
);

const PortefeuilleCard = ({
  totalClients,
  dossierIncomplet,
  kycAValider,
  maintienAValider,
  onNav,
}) => (
  <Card onClick={() => onNav && onNav("portef")}>
    <CardHeader icon={Folder}>Portefeuille</CardHeader>
    {totalClients === 0 ? (
      <EmptyState message="Aucun client dans votre portefeuille" />
    ) : (
      <>
        <div className="mt-3">
          <BigNumber value={totalClients} />
          <SubLabel> clients actifs</SubLabel>
        </div>
        <div className="mt-5">
          <DotRow
            label="Dossier incomplet"
            value={dossierIncomplet}
            colorClass="bg-red-500"
          />
          <DotRow
            label="KYC à valider"
            value={kycAValider}
            colorClass="bg-amber-400"
          />
          <DotRow
            label="Maintien à valider"
            value={maintienAValider}
            colorClass="bg-amber-400"
          />
        </div>
      </>
    )}
  </Card>
);

const PipelineCard = ({ leadsTotal, leadOrder, leads, leadMax, onNav }) => (
  <Card onClick={() => onNav && onNav("lead")}>
    <CardHeader icon={LeadIcon}>Pipeline Lead</CardHeader>
    {leadsTotal === 0 ? (
      <EmptyState message="Aucun prospect en cours" />
    ) : (
      <>
        <div className="mt-3">
          <BigNumber value={leadsTotal} />
          <SubLabel> prospects en cours</SubLabel>
        </div>
        <div className="mt-5">
          {leadOrder.map((k) => (
            <PipelineRow key={k} label={k} value={leads[k]} max={leadMax} />
          ))}
        </div>
      </>
    )}
  </Card>
);

const BilanCard = ({
  bilanPct,
  bilanDone,
  bilanInProgress,
  bilanLate,
  bilanTotal,
  irPct,
  irDone,
  irInProgress,
  irLate,
  irTotal,
  agoPct,
  agoDone,
  agoInProgress,
  agoLate,
  agoTotal,
  onNav,
}) => {
  const hasData = bilanTotal > 0 || irTotal > 0 || agoTotal > 0;
  return (
    <Card onClick={() => onNav && onNav("suivi")}>
      <CardHeader icon={TrendingUp}>
        Avancement campagne {BILAN_YEAR}
      </CardHeader>
      {!hasData ? (
        <EmptyState message={`Aucun bilan enregistré pour ${BILAN_YEAR}`} />
      ) : (
        <div className="mt-3 divide-y divide-gray-100">
          {bilanTotal > 0 && (
            <BilanSection
              label="Bilan annuaire"
              pct={bilanPct}
              done={bilanDone}
              inProgress={bilanInProgress}
              late={bilanLate}
              total={bilanTotal}
            />
          )}
          {irTotal > 0 && (
            <BilanSection
              label="Bilan IR"
              pct={irPct}
              done={irDone}
              inProgress={irInProgress}
              late={irLate}
              total={irTotal}
              lateLabel="autre"
            />
          )}
          {agoTotal > 0 && (
            <BilanSection
              label="bilan AGO"
              pct={agoPct}
              done={agoDone}
              inProgress={agoInProgress}
              late={agoLate}
              total={agoTotal}
            />
          )}
        </div>
      )}
    </Card>
  );
};

const TodoSection = ({ collabRanked, onNav }) => {
  const max = collabRanked[0]?.retard || 1;
  return (
    <Card onClick={() => onNav && onNav("todo")}>
      <div className="flex justify-between items-center mb-6">
        <SectionTitle icon={CheckSquare}>
          ToDo — collaborateurs en retard
        </SectionTitle>
        <OpenButton onClick={() => onNav && onNav("todo")} />
      </div>
      {collabRanked.length === 0 ? (
        <EmptyState message="Aucun collaborateur en retard" />
      ) : (
        collabRanked.map((c, i) => (
          <RankRow
            key={c.id}
            rank={i + 1}
            initials={c.initials}
            name={c.name}
            retard={c.retard}
            max={max}
          />
        ))
      )}
    </Card>
  );
};

const dotColorByStatut = (statut) => {
  if (statut === "Terminé") return "bg-green-500";
  if (statut === "Bloqué") return "bg-red-500";
  if (statut === "Signature") return "bg-blue-500";
  if (statut === "Rédaction") return "bg-indigo-400";
  return "bg-[#840040]";
};

const JuridiqueCard = ({ juridique, onNav }) => {
  const { enRetard = 0, parStatut = {} } = juridique || {};
  const statutEntries = Object.entries(parStatut).filter(
    ([statut]) => statut !== "Archiver"
  );
  const totalSansArchives = statutEntries.reduce((sum, [, c]) => sum + c, 0);
  const enCoursSansArchives = statutEntries
    .filter(([s]) => s !== "Terminé")
    .reduce((sum, [, c]) => sum + c, 0);

  return (
    <Card onClick={() => onNav && onNav("suivi_juridique")}>
      <CardHeader icon={Scale}>Juridique</CardHeader>
      <div className="mt-3">
        <div className="flex items-baseline gap-1 flex-wrap">
          <BigNumber value={enCoursSansArchives} />
          <SubLabel>/ {totalSansArchives}</SubLabel>
          <SubLabel> missions en cours</SubLabel>
        </div>
        {enRetard > 0 && (
          <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
            ⊙ {enRetard} en retard
          </span>
        )}
      </div>
      {statutEntries.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5">
          {statutEntries.map(([statut, count]) => (
            <div key={statut} className="flex items-center gap-2">
              <Dot colorClass={dotColorByStatut(statut)} />
              <span className="text-[10px] text-gray-500 flex-1 truncate">
                {statut}
              </span>
              <strong className="text-[10px] text-gray-800 flex-shrink-0">
                {count}
              </strong>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const LMRelanceCard = ({ relances, onNav, onIntercept }) => (
  <Card onClick={() => onNav && onNav("lead")}>
    <CardHeader icon={BellRing}>Relances à venir</CardHeader>
    {relances.length === 0 ? (
      <EmptyState message="Relances à venir" />
    ) : (
      <>
        <div className="mt-3">
          <BigNumber value={relances.length} />
          <SubLabel>
            {" "}
            proposition{relances.length > 1 ? "s" : ""} de LM sera
            {relances.length > 1 ? "nt" : ""} relancée
            {relances.length > 1 ? "s" : ""} automatiquement demain
          </SubLabel>
        </div>
        <div className="mt-5">
          {relances.map((doc) => (
            <RelanceRow
              key={doc.id}
              name={getDocDisplayName(doc)}
              type={getDocLMType(doc)}
              createdAt={doc.createdAt}
              onIntercept={() => onIntercept && onIntercept(doc)}
            />
          ))}
        </div>
      </>
    )}
  </Card>
);

/* ============================================================
   PAGE — assemble les organisms avec les données
============================================================ */

const BILAN_YEAR = 2025;

const isOverdue = (todo) => {
  if (!todo?.dueDate) return false;
  try {
    return (
      isPast(startOfDay(parseISO(todo.dueDate))) &&
      todo.status !== "Terminer" &&
      todo.status !== "Archiver"
    );
  } catch {
    return false;
  }
};

// Même calcul que le badge "Relancer" de PropositionLMLead.js (J-7), décalé
// à J-6 pour prévenir la veille de la relance automatique.
const getDaysDiff = (date) => {
  const today = new Date();
  const created = new Date(date);
  today.setHours(0, 0, 0, 0);
  created.setHours(0, 0, 0, 0);
  return Math.floor((today - created) / (1000 * 60 * 60 * 24));
};

const getDocDisplayName = (doc) => {
  const name = doc.emailSubject?.split(" - ")[0]?.trim();
  return !name || name.toLowerCase() === "n/a"
    ? `${doc.nom || "-"} ${doc.prenom || "-"}`
    : name;
};

// Type de LM : même convention que PropositionLMLead / LMLead (partie après " - ")
const getDocLMType = (doc) => doc.emailSubject?.split(" - ")[1]?.trim() || "";

function Dashboard({ onNav }) {
  const {
    userDashboardData,
    loadingUserDashboard,
    todos,
    utilisateur,
    leads,
    documents,
    setDocuments,
    fetchDocumentsLM,
  } = useEtatGlobal();

  const [interceptTransition, setInterceptTransition] = useState(null);

  const showAll = utilisateur?.fonctionalite?.includes("Admin") || false;
  const hasFeature = (feature) =>
    utilisateur?.fonctionalite?.includes(feature) || showAll;

  const handleInterceptClick = (doc) => {
    setInterceptTransition({ doc, to: "lm_signee" });
  };

  const handleInterceptCancel = () => {
    setInterceptTransition(null);
  };

  const handleInterceptConfirm = (extraData) => {
    const { doc } = interceptTransition;
    setInterceptTransition(null);

    // Mise à jour optimiste sur le state global `documents` : Dashboard et
    // AllLead (PropLM/DocumentsTable) le lisent tous les deux depuis le même
    // contexte, donc la carte disparaît de "Proposition LM" et apparaît dans
    // "LM signée" instantanément, sans attendre la réponse serveur.
    setDocuments((prev) =>
      prev.map((d) =>
        d.id === doc.id
          ? { ...d, specificite: "completed", status: "completed" }
          : d
      )
    );

    (async () => {
      try {
        const formData = new FormData();
        if (extraData.dateSignature)
          formData.append("dateSignature", extraData.dateSignature);

        let fileToUpload = extraData.signedPdfFile || null;
        if (fileToUpload && fileToUpload.size > 4 * 1024 * 1024) {
          try {
            const { PDFDocument } = await import("pdf-lib");
            const arrayBuffer = await fileToUpload.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer, {
              updateMetadata: false,
            });
            const compressed = await pdfDoc.save({ useObjectStreams: true });
            const compressedFile = new File([compressed], fileToUpload.name, {
              type: "application/pdf",
            });
            if (compressedFile.size < fileToUpload.size)
              fileToUpload = compressedFile;
          } catch (comprErr) {
            console.warn(
              "Compression PDF échouée, envoi du fichier original :",
              comprErr
            );
          }
        }
        if (fileToUpload) formData.append("signedPdf", fileToUpload);

        await authFetch(
          `https://backend-myalfa.vercel.app/api/envelope/${doc.id}/complete-manual`,
          { method: "PUT", body: formData }
        );
        fetchDocumentsLM();
      } catch (err) {
        console.error("Erreur interception LM :", err);
        setDocuments((prev) =>
          prev.map((d) =>
            d.id === doc.id
              ? { ...d, specificite: doc.specificite, status: doc.status }
              : d
          )
        );
      }
    })();
  };

  const cabinetId = getCabinetIdFilter(utilisateur);
  const filteredDocuments = documents.filter(
    (d) => !cabinetId || d.formLead?.cabinetId === cabinetId
  );

  // Portefeuille — nombre de clients vient directement de l'API
  const totalClients = userDashboardData?.nombreClients ?? 0;
  const giLabFinis = userDashboardData?.giLabFinis ?? 0;
  const giLabEnCours = userDashboardData?.giLabEnCours ?? 0;
  const maintenanceAValider = userDashboardData?.maintenanceAValider ?? 0;
  const dossierIncomplet = totalClients - (giLabFinis + giLabEnCours);
  const kycAValider = giLabEnCours;

  // Pipeline — utilise les leads et documents du contexte global
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const priseContact = leads.filter((l) => l.specificite === "contact").length;
  const rdvAvenir = leads.filter(
    (l) =>
      l.specificite === "rdv" &&
      l.status !== "LM" &&
      new Date(l.dateRDV) >= today
  ).length;
  const rdvTermine = leads.filter(
    (l) =>
      l.specificite === "rdv" &&
      l.status !== "LM" &&
      new Date(l.dateRDV) < today
  ).length;
  const propLM = filteredDocuments.filter(
    (d) =>
      d.specificite !== "completed" &&
      d.specificite !== "archive" &&
      d.status !== "completed"
  ).length;
  const lmSigne = filteredDocuments.filter(
    (d) =>
      d.specificite === "completed" ||
      (d.status === "completed" && d.specificite !== "archive")
  ).length;

  // LM en Proposition à J-6 : seront relancées automatiquement demain (J-7)
  const lmRelanceDemain = filteredDocuments.filter(
    (d) =>
      d.specificite !== "completed" &&
      d.specificite !== "archive" &&
      d.status !== "completed" &&
      getDaysDiff(d.createdAt) === 6
  );

  const leadsTotal = priseContact + rdvAvenir + rdvTermine + propLM + lmSigne;
  const leadOrder = [
    "Prise de contact",
    "RDV à venir",
    "RDV terminé",
    "Proposition LM",
    "LM signée",
  ];
  const leadsMap = {
    "Prise de contact": priseContact,
    "RDV à venir": rdvAvenir,
    "RDV terminé": rdvTermine,
    "Proposition LM": propLM,
    "LM signée": lmSigne,
  };
  const leadMax = Math.max(
    priseContact,
    rdvAvenir,
    rdvTermine,
    propLM,
    lmSigne,
    1
  );

  // Bilan — données prêtes à l'emploi depuis l'API
  const bilanYear = userDashboardData?.bilanParAnnee?.[BILAN_YEAR] || {};
  const bilanStats = {
    total: bilanYear.total || 0,
    finalise: bilanYear.finalise || 0,
    enCours: bilanYear.enCours || 0,
    enRetard: bilanYear.enRetard || 0,
    pct: bilanYear.pct || 0,
  };

  // BilanIR
  const bilanIRYear = userDashboardData?.bilanIR?.[BILAN_YEAR] || {};
  const irTotal = bilanIRYear.total || 0;
  const irFinalise = bilanIRYear.finalise || 0;
  const irEnCours =
    (bilanIRYear.enCours || 0) +
    (bilanIRYear.aFaire || 0) +
    (bilanIRYear.enAttenteClient || 0);
  const irAutre = bilanIRYear.autre || 0;
  const irPct = irTotal > 0 ? Math.round((irFinalise / irTotal) * 100) : 0;

  // BilanAGO
  const bilanAGOYear = userDashboardData?.bilanAGO?.[BILAN_YEAR] || {};
  const agoTotal = bilanAGOYear.total || 0;
  const agoEnCours = bilanAGOYear.enCours || 0;
  const agoEnRetard = bilanAGOYear.enRetard || 0;
  const agoFinalise = Math.max(0, agoTotal - agoEnCours);
  const agoInProgress = Math.max(0, agoEnCours - agoEnRetard);
  const agoPct = agoTotal > 0 ? Math.round((agoFinalise / agoTotal) * 100) : 0;

  // Juridique
  const juridique = userDashboardData?.juridique;

  // Todo — collaborateurs en retard (hors utilisateur connecté)
  const currentUserKey = `${utilisateur?.name || ""} ${
    utilisateur?.lastName || ""
  }`.trim();
  const collabRanked = useMemo(() => {
    return todos
      .filter((userBlock) => userBlock.userKey !== currentUserKey)
      .map((userBlock) => {
        const retard = userBlock.todos.filter((t) => isOverdue(t)).length;
        const initials = `${userBlock.name?.[0] || ""}${
          userBlock.lastName?.[0] || ""
        }`.toUpperCase();
        return {
          id: userBlock.userKey,
          name: `${userBlock.name} ${userBlock.lastName}`.trim(),
          initials,
          retard,
        };
      })
      .sort((a, b) => b.retard - a.retard);
  }, [todos, currentUserKey]);

  if (loadingUserDashboard && !userDashboardData) {
    return (
      <div className="min-h-screen bg-[#faf6f1] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-[#840040] border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-gray-400">
            Chargement du tableau de bord…
          </p>
        </div>
      </div>
    );
  }

  if (!loadingUserDashboard && !userDashboardData) {
    return (
      <div className="min-h-screen bg-[#faf6f1] flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center max-w-sm">
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Données indisponibles
          </p>
          <p className="text-xs text-gray-400">
            Impossible de charger le tableau de bord. Actualisez la page ou
            contactez le support via un ticket.
          </p>
        </div>
      </div>
    );
  }

  const canPortefeuille = hasFeature("Portefeuille");
  const canLead = hasFeature("Lead");
  const canSuivi = hasFeature("Suivi Prod");

  const topCards = [true, canLead, canSuivi].filter(Boolean).length;
  const gridCols =
    topCards === 1
      ? "grid-cols-1 max-w-sm"
      : topCards === 2
      ? "grid-cols-2"
      : "grid-cols-3";

  return (
    <div className="min-h-screen bg-[#faf6f1]">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900 m-0">
          Tableau de bord
        </h1>
        <p className="text-xs text-gray-400 mt-1">Vue d'ensemble du cabinet</p>
      </div>

      <div className={`grid ${gridCols} gap-5 mb-5`}>
        <PortefeuilleCard
          totalClients={totalClients}
          dossierIncomplet={dossierIncomplet}
          kycAValider={kycAValider}
          maintienAValider={maintenanceAValider}
          onNav={canPortefeuille ? onNav : null}
        />
        {canLead && (
          <PipelineCard
            leadsTotal={leadsTotal}
            leadOrder={leadOrder}
            leads={leadsMap}
            leadMax={leadMax}
            onNav={onNav}
          />
        )}
        {canSuivi && (
          <BilanCard
            bilanPct={bilanStats.pct}
            bilanDone={bilanStats.finalise}
            bilanInProgress={bilanStats.enCours}
            bilanLate={bilanStats.enRetard}
            bilanTotal={bilanStats.total}
            irPct={irPct}
            irDone={irFinalise}
            irInProgress={irEnCours}
            irLate={irAutre}
            irTotal={irTotal}
            agoPct={agoPct}
            agoDone={agoFinalise}
            agoInProgress={agoInProgress}
            agoLate={agoEnRetard}
            agoTotal={agoTotal}
            onNav={onNav}
          />
        )}
      </div>

      <div
        className={`grid ${canSuivi ? "grid-cols-3" : "grid-cols-1"} gap-5 ${
          canLead ? "mb-5" : ""
        }`}
      >
        {canSuivi && (
          <div className="col-span-1">
            <JuridiqueCard juridique={juridique} onNav={onNav} />
          </div>
        )}
        <div className={canSuivi ? "col-span-2" : "col-span-1"}>
          {canLead ? (
            <LMRelanceCard
              relances={lmRelanceDemain}
              onNav={onNav}
              onIntercept={handleInterceptClick}
            />
          ) : (
            <TodoSection collabRanked={collabRanked} onNav={onNav} />
          )}
        </div>
      </div>

      {canLead && <TodoSection collabRanked={collabRanked} onNav={onNav} />}

      {interceptTransition && (
        <DragDocTransitionOverlay
          transition={interceptTransition}
          onConfirm={handleInterceptConfirm}
          onCancel={handleInterceptCancel}
        />
      )}
    </div>
  );
}

export default Dashboard;
